<template>
  <b-card no-body class="mb-2">
    <b-card-body>
      <h6 class="mb-1">Отрывки с YouTube</h6>

      <div class="d-flex flex-wrap align-items-center" style="gap: 8px">
        <b-form-input
            v-model="query"
            placeholder="Запрос для поиска…"
            style="max-width: 400px"
            :disabled="isSearching"
            @keyup.enter="search"
        />
        <b-button
            variant="primary"
            :disabled="!query.trim() || isSearching"
            @click="search"
        >
          <b-spinner v-if="isSearching" small class="mr-50" />
          <feather-icon v-else icon="SearchIcon" class="mr-50" />
          Найти
        </b-button>
      </div>

      <div v-if="isSearching" class="text-center text-muted py-2">
        <b-spinner label="Loading..." class="mb-50 d-block mx-auto" />
        Ищем видео на YouTube… это может занять полминуты
      </div>

      <template v-else-if="searched">
        <div v-if="results.length" class="video-results mt-1">
          <div
              v-for="video in results"
              :key="video.youtube_external_id"
              class="video-result"
              :class="{ 'video-result-selected': selectedVideo && selectedVideo.youtube_external_id === video.youtube_external_id }"
              @click="selectVideo(video)"
          >
            <div class="video-thumb">
              <b-img v-if="video.preview" :src="video.preview" loading="lazy" />
              <div v-else class="video-thumb-placeholder">
                <feather-icon icon="VideoIcon" size="24" />
              </div>
              <b-badge v-if="video.duration" variant="dark" class="video-duration">
                {{ formatDuration(video.duration) }}
              </b-badge>
            </div>
            <div class="video-title" :title="video.title">{{ video.title }}</div>
            <div class="video-channel text-muted">{{ video.channel }}</div>
          </div>
        </div>
        <div v-else class="text-center text-muted py-1">
          Ничего не нашлось
        </div>
      </template>

      <div v-if="selectedVideo" class="mt-1">
        <b-embed
            type="iframe"
            aspect="16by9"
            :src="`https://www.youtube.com/embed/${selectedVideo.youtube_external_id}`"
            allowfullscreen
            class="video-preview-frame"
        />

        <b-overlay :show="isExtracting" rounded="sm" class="mt-1">
          <template #overlay>
            <div class="text-center">
              <b-spinner label="Loading..." class="mb-50" />
              <div>Извлекаем аудио из видео… это может занять до минуты</div>
            </div>
          </template>

          <b-button
              variant="primary"
              :disabled="isExtracting"
              @click="extractAudio"
          >
            <feather-icon icon="DownloadIcon" class="mr-50" />
            Загрузить аудио
          </b-button>
        </b-overlay>
      </div>

      <div v-if="extracted && previewUrl" class="mt-1">
        <strip-audio-cutter
            v-model="audioRegion"
            :audio-url="previewUrl"
            :default-start="0"
            :default-duration="DEFAULT_DURATION_SECONDS"
        />

        <b-form-group label="Название" class="mt-1 mb-50">
          <b-form-input v-model="title" maxlength="255" />
        </b-form-group>

        <b-button
            variant="success"
            :disabled="!canSave || isSaving"
            @click="save"
        >
          <b-spinner v-if="isSaving" small class="mr-50" />
          <feather-icon v-else icon="SaveIcon" class="mr-50" />
          Сохранить в диалоги
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BSpinner,
  BButton,
  BOverlay,
  BFormGroup,
  BFormInput,
  BImg,
  BBadge,
  BEmbed,
} from 'bootstrap-vue'
import store from '@/store'
import { ref, computed, onBeforeUnmount } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import StripAudioCutter from '@/views/strip/builder/StripAudioCutter.vue'

const DEFAULT_DURATION_SECONDS = 40

export default {
  name: 'DialogueAudioExtractor',
  components: {
    BCard,
    BCardBody,
    BSpinner,
    BButton,
    BOverlay,
    BFormGroup,
    BFormInput,
    BImg,
    BBadge,
    BEmbed,
    StripAudioCutter,
  },
  props: {
    movieId: { type: Number, required: true },
    movieTitle: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const toast = useToast()

    const query = ref(props.movieTitle ? `${props.movieTitle} отрывок` : '')
    const isSearching = ref(false)
    const searched = ref(false)
    const results = ref([])
    const selectedVideo = ref(null)

    const isExtracting = ref(false)
    const extracted = ref(null) // {tmp_path, url, duration}
    // Blob-URL превью: mp3 идёт через бэкенд (напрямую с S3 браузер не отдаст — CORS),
    // а blob-URL не требует ни CORS, ни https
    const previewUrl = ref(null)
    const audioRegion = ref({ startSeconds: 0, durationSeconds: DEFAULT_DURATION_SECONDS })
    const title = ref('')
    const isSaving = ref(false)

    const releasePreviewUrl = () => {
      if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value)
        previewUrl.value = null
      }
    }

    const canSave = computed(() => !!extracted.value
      && !!title.value.trim()
      && audioRegion.value.durationSeconds > 0)

    const formatDuration = seconds => {
      const minutes = Math.floor(seconds / 60)
      const rest = Math.floor(seconds % 60)
      return `${minutes}:${String(rest).padStart(2, '0')}`
    }

    const search = () => {
      if (!query.value.trim() || isSearching.value) return
      isSearching.value = true
      selectedVideo.value = null
      extracted.value = null
      releasePreviewUrl()
      store.dispatch('movies/searchYoutubeVideos', {
        movieId: props.movieId,
        query: query.value.trim(),
      })
        .then(response => {
          results.value = response.data.data
          searched.value = true
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Ошибка при поиске видео на YouTube',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => { isSearching.value = false })
    }

    const selectVideo = video => {
      selectedVideo.value = video
      // Ранее извлечённое аудио относится к другому видео — сбрасываем
      extracted.value = null
      releasePreviewUrl()
    }

    const extractAudio = () => {
      if (!selectedVideo.value) return
      // Захватываем видео ДО долгого dispatch: пользователь может успеть выбрать
      // другой результат, пока extract ещё выполняется (20-60 сек)
      const requestedVideo = selectedVideo.value
      isExtracting.value = true
      extracted.value = null
      releasePreviewUrl()
      store.dispatch('movies/extractYoutubeAudio', {
        movieId: props.movieId,
        videoId: requestedVideo.youtube_external_id,
      })
        .then(response => store
          .dispatch('movies/fetchTrailerTmpAudio', {
            movieId: props.movieId,
            tmpPath: response.data.tmp_path,
          })
          .then(audioResponse => {
            previewUrl.value = URL.createObjectURL(audioResponse.data)
            extracted.value = response.data
            const duration = response.data.duration || DEFAULT_DURATION_SECONDS
            audioRegion.value = {
              startSeconds: 0,
              durationSeconds: Math.min(DEFAULT_DURATION_SECONDS, duration),
            }
            title.value = requestedVideo.title || ''
          }))
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Не удалось извлечь аудио из видео',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => { isExtracting.value = false })
    }

    const save = () => {
      if (!canSave.value) return
      const start = audioRegion.value.startSeconds
      const end = start + audioRegion.value.durationSeconds
      isSaving.value = true
      store.dispatch('movies/saveTrailerAudioFromVideo', {
        movieId: props.movieId,
        tmpPath: extracted.value.tmp_path,
        start,
        end,
        title: title.value.trim(),
        category: 'dialogue',
      })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Аудио сохранено в диалоги',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          emit('saved')
          extracted.value = null
          releasePreviewUrl()
          title.value = ''
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Не удалось сохранить аудио',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => { isSaving.value = false })
    }

    onBeforeUnmount(releasePreviewUrl)

    return {
      DEFAULT_DURATION_SECONDS,
      query,
      isSearching,
      searched,
      results,
      selectedVideo,
      isExtracting,
      extracted,
      previewUrl,
      audioRegion,
      title,
      isSaving,
      canSave,
      formatDuration,
      search,
      selectVideo,
      extractAudio,
      save,
    }
  },
}
</script>

<style lang="scss" scoped>
.video-results {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.video-result {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 0.5rem;
  padding: 4px;

  &:hover {
    background-color: rgba(115, 103, 240, 0.08);
  }
}

.video-result-selected {
  border-color: #7367f0;
}

.video-thumb {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 0.4rem;
  overflow: hidden;
  background-color: #f8f8f8;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.video-thumb-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b9b9c3;
}

.video-duration {
  position: absolute;
  bottom: 5px;
  right: 5px;
  pointer-events: none;
}

.video-title {
  margin-top: 6px;
  font-size: 0.9rem;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-channel {
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.video-preview-frame {
  max-width: 640px;
  border-radius: 0.5rem;
  overflow: hidden;
}
</style>
